import React, { useCallback, useEffect, type FC } from "react";
import { useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import { isSettingOpenAtom, standbyModeAtom } from "~/Atom";
import { talkModes } from "~/Commons";
import ReceptionStandbyScreen from "~/components/modal/ReceptionStandbyScreen";
import ReceptionHeader from "~/components/ReceptionHeader";

const INTERACTIVE_TARGET_SELECTOR = [
  '[data-reception-interactive="true"]',
  "button",
  "select",
  "input",
  "textarea",
  "a",
  '[role="button"]',
  '[role="link"]',
].join(", ");

type ReceptionLoadingLocationState = {
  skipStartupModal?: boolean;
};

/**
 * Reception loading page (formerly ReceptionPage).
 * Shows standby screen and right sidebar, and sets standby mode on mount.
 */
const ReceptionLoadingPage: FC = () => {
  const [, setStandbyMode] = useAtom(standbyModeAtom);
  const [isSettingOpen] = useAtom(isSettingOpenAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const pageNavigationHandlerRef = React.useRef<(() => void) | null>(null);
  const standbyRefreshTimerRef = React.useRef<number | null>(null);
  const skipStartupModal =
    (location.state as ReceptionLoadingLocationState | null)?.skipStartupModal === true;
  const skipStartupModalForCurrentMountRef = React.useRef(skipStartupModal);
  const hasConsumedSkipStartupModalRef = React.useRef(false);

  const registerPageNavigationHandler = useCallback((handler: (() => void) | null) => {
    pageNavigationHandlerRef.current = handler;
  }, []);

  const handlePageMouseUp = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.defaultPrevented || isSettingOpen) {
        return;
      }

      if (event.target instanceof Element && event.target.closest(INTERACTIVE_TARGET_SELECTOR)) {
        return;
      }

      pageNavigationHandlerRef.current?.();
    },
    [isSettingOpen]
  );

  // skipStartupModal はこのマウント中だけ有効にし、直後に history state から取り除く。
  // これにより、同一表示中はモーダル抑止を維持しつつ、リロードや次回遷移では通常起動に戻せる。
  useEffect(() => {
    if (!skipStartupModalForCurrentMountRef.current || hasConsumedSkipStartupModalRef.current) {
      return;
    }

    hasConsumedSkipStartupModalRef.current = true;

    const nextState = { ...((location.state as ReceptionLoadingLocationState | null) ?? {}) };
    delete nextState.skipStartupModal;

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    });
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    // popstate/hashchange 時はページ全体を再読み込みせず、standbyMode を
    // stopped -> standby と切り替えて待受状態だけを再初期化する。
    const refreshStandbyMode = () => {
      if (standbyRefreshTimerRef.current !== null) {
        window.clearTimeout(standbyRefreshTimerRef.current);
      }

      setStandbyMode(talkModes.stopped);
      standbyRefreshTimerRef.current = window.setTimeout(() => {
        setStandbyMode(talkModes.standby);
        standbyRefreshTimerRef.current = null;
      }, 0);
    };

    window.addEventListener("popstate", refreshStandbyMode);
    window.addEventListener("hashchange", refreshStandbyMode);
    setStandbyMode(talkModes.standby);

    return () => {
      if (standbyRefreshTimerRef.current !== null) {
        window.clearTimeout(standbyRefreshTimerRef.current);
      }
      window.removeEventListener("popstate", refreshStandbyMode);
      window.removeEventListener("hashchange", refreshStandbyMode);
    };
  }, [setStandbyMode]);

  return (
    /* biome-ignore lint/a11y/noStaticElementInteractions: loading page handles background taps outside interactive controls */
    <div className="reception-loading-page" onMouseUp={handlePageMouseUp}>
      <ReceptionHeader />
      <ReceptionStandbyScreen
        registerPageNavigationHandler={registerPageNavigationHandler}
        skipStartupModal={skipStartupModalForCurrentMountRef.current}
      />
    </div>
  );
};

export default ReceptionLoadingPage;
